import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { largeNumberFormatter } from 'shared/base';

import { TOTAL_LABEL } from '../../constants';
import { PieChartData } from '../../types';

export const PieChartTooltipContent = ({
  colorWheel,
  primaryData,
  secondaryData,
  selectedName,
  showSecondary,
  total,
}: {
  colorWheel: string[];
  primaryData: PieChartData;
  secondaryData?: PieChartData;
  selectedName: string | undefined;
  showSecondary: boolean;
  total: number;
}) => {
  const data = showSecondary ? secondaryData : primaryData;
  const selectionTotal =
    showSecondary && secondaryData
      ? secondaryData.reduce((acc, curr) => acc + curr.value, 0)
      : total;

  const items = [...(data ?? []), { label: TOTAL_LABEL, name: TOTAL_LABEL, value: selectionTotal }];

  const ITEM_HEIGHT = 25;
  const VH_90_IN_PX = Math.floor(window.innerHeight * 0.9);
  const itemsPerColumn = Math.floor(VH_90_IN_PX / ITEM_HEIGHT);

  const numColumns = Math.ceil(items.length / itemsPerColumn);

  const columns = Array.from({ length: numColumns }, (_, colIndex) => {
    const startIdx = colIndex * itemsPerColumn;
    const endIdx = Math.min(startIdx + itemsPerColumn, items.length);

    return items.slice(startIdx, endIdx);
  });

  const renderItems = (itemsToRender: typeof items, startIndex = 0) => {
    return itemsToRender.map(({ label, name, value }, index) => {
      const actualIndex = startIndex + index;
      const showChip = label !== TOTAL_LABEL;

      return (
        <Box
          key={`${label}-${actualIndex}`}
          alignItems="center"
          display="flex"
          sx={{
            backgroundColor: selectedName === name ? 'primary.selected' : 'transparent',
            borderRadius: 0.25,
            mt: actualIndex === (data?.length ?? 0) ? 0.5 : 0,
            px: 0.5,
            width: '100%',
          }}
        >
          <Box alignItems="center" display="flex" gap={1} sx={{ width: '5%' }}>
            {showChip && (
              <Box
                sx={{
                  backgroundColor: colorWheel[actualIndex % colorWheel.length],
                  borderRadius: '2px',
                  height: '14px',
                  width: '7px',
                }}
              />
            )}
          </Box>
          <Box alignItems="center" display="flex" gap={1} sx={{ width: '55%' }}>
            <Typography color="text.primary" noWrap={true} variant="body2">
              {label}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', width: '25%' }}>
            <Typography color="text.primary" variant="body2Bold">
              {largeNumberFormatter.format(value)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', width: '15%' }}>
            <Typography color="text.secondary" variant="body2">
              {((value / selectionTotal) * 100).toFixed(0)}%
            </Typography>
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box
      p={1.5}
      sx={{
        display: 'flex',
        gap: 2,
        maxWidth: `${numColumns * 300}px`,
        overflowY: 'auto',
      }}
    >
      {columns.map((columnItems, colIndex) => (
        <Box
          key={`column-${colIndex}`}
          sx={{
            width: '300px',
          }}
        >
          {renderItems(columnItems, colIndex * itemsPerColumn)}
        </Box>
      ))}
    </Box>
  );
};
